import { useCallback, useEffect, useRef, useState } from 'react';
import _ from "lodash";

import { IAPIResponseType } from "@/models/api.model";
import { ITableColumn, ITableComponentProps } from "@/models/table.model";
import { CommonService } from "@/services";
import "./TableWrapperComponent.scss";
import TableComponent from "@/components/common-components/table/TableComponent";
import PaginationComponent from "@/components/common-components/pagination/PaginationComponent";

export interface TableWrapperComponentProps extends ITableComponentProps {
    moduleName?: string;
    type?: "custom";
    showAddButton?: boolean;
    url: string;
    method: "get" | "post" | string;
    isPaginated?: boolean;
    extraPayload?: any;
    refreshToken?: string;
    autoHeight?: boolean;
    onDataLoaded?: (data: any[]) => void;
    columns: ITableColumn[];
    noDataText?: string;
    errorText?: string;
    className?: string;
    dataExtractorKey?: string;
}

const TableWrapperComponent = (props: TableWrapperComponentProps) => {
    const {
        refreshToken,
        moduleName,
        id,
        url,
        method,
        extraPayload,
        noDataText,
        onDataLoaded,
        columns,
        className,
        rowClassName,
        rowKey,
        errorText,
        dataExtractorKey
    } = props;

    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [isDataLoadingFailed, setIsDataLoadingFailed] = useState<boolean>(false);
    const [data, setData] = useState<any[]>([]);
    const [metaInfo, setMetaInfo] = useState<any>({});
    const pageNumRef = useRef<number>(1);
    const totalResultsRef = useRef<number>(0);
    const pageSizeRef = useRef<number>(10);
    const isPaginated = props.isPaginated ?? false;
    const APICallSubscription = useRef<any>(null);

    const getListData = useCallback(() => {
        const cancelTokenSource = CommonService.getCancelToken();
        const payload = isPaginated ? _.cloneDeep({ page_size: pageSizeRef.current, ...extraPayload, page: pageNumRef.current }) : "";

        // Handle sorting logic
        if (payload?.sort && payload?.sort?.key) {
            payload.sort[payload.sort.key] = payload?.sort?.order;
            delete payload.sort.key;
            delete payload.sort.order;
        } else {
            delete payload.sort;
        }

        let apiCall;
        if (method === "post") {
            apiCall = CommonService._api.post(url, payload, { cancelToken: cancelTokenSource.token });
        } else {
            apiCall = CommonService._api.get(url, payload, { cancelToken: cancelTokenSource.token });
        }

        if (APICallSubscription && APICallSubscription.current) {
            APICallSubscription.current.cancel();
        }

        APICallSubscription.current = cancelTokenSource;
        setIsDataLoading(true);
        setIsDataLoadingFailed(false);

        let listData: any[] = [];
        let metaInfo = {};

        apiCall.then((response: IAPIResponseType<any>) => {
            if (response) {
                if (isPaginated) {
                    listData = response?.data?.[dataExtractorKey ?? ''] || response?.data || [];
                    totalResultsRef.current = +response?.data?.pagination?.total;
                    pageNumRef.current = +response?.data?.pagination?.page;
                    pageSizeRef.current = +response?.data?.pagination?.page_size;
                } else {
                    listData = response?.data?.[dataExtractorKey ?? 'docs'] || response?.data || [];
                }
                metaInfo = response?.data?.meta || {};
            }

            setData(listData);
            setMetaInfo(metaInfo);
            if(onDataLoaded){
                onDataLoaded(listData);
            }
            setIsDataLoading(false);
            setIsDataLoadingFailed(false);
        }).catch(() => {
            setData([]);
            setIsDataLoading(false);
            setIsDataLoadingFailed(true);
        });
    }, [isPaginated, method, url, extraPayload, onDataLoaded, dataExtractorKey]);

    useEffect(() => {
        if (extraPayload?.page) {
            pageNumRef.current = extraPayload.page;
        }
        getListData();
    }, [getListData, refreshToken, extraPayload]);

    const handlePageNumberChange = useCallback((newPage: number) => {
        pageNumRef.current = newPage;
        getListData();
    }, [getListData]);
    
    const handlePageSizeChange = useCallback((limit: number) => {
        pageSizeRef.current = limit;
        pageNumRef.current = 1;
        getListData();
    }, [getListData]);

    useEffect(() => {
        const sub = CommonService._communications.TableWrapperRefreshSubject.subscribe((data) => {
            if (data.moduleName === moduleName) {
                getListData();
            }
        });

        return () => {
            sub.unsubscribe();
        }
    }, [getListData, moduleName]);

    return (
        <div className={`shadcn-table-wrapper-component ${className || ''}`}>
            <div className="relative">
                <TableComponent
                    id={id}
                    columns={columns}
                    data={data}
                    metaInfo={metaInfo}
                    loading={isDataLoading}
                    errored={isDataLoadingFailed}
                    rowKey={rowKey}
                    rowClassName={rowClassName}
                    noDataText={noDataText}
                    errorText={errorText}
                />
            </div>
        
            {isPaginated && (
                <div className="pagination-wrapper">
                    {data && (
                        <PaginationComponent
                            paginationOptions={[10, 20, 50, 100]}
                            totalResults={totalResultsRef.current}
                            limit={pageSizeRef.current}
                            page={pageNumRef.current}
                            onPageChange={handlePageNumberChange}
                            onRowsPerPageChange={handlePageSizeChange}
                            id={`${id}_pagination`}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default TableWrapperComponent;

// ****************************** USAGE ****************************** //

// <TableWrapperComponent
//     columns={[
//         {
//             key: "name",
//             dataIndex: "name",
//             title: "Name"
//         },
//         {
//             key: "description",
//             dataIndex: "description",
//             title: "Description"
//         }
//     ]}
//     rowKey={(record: any) => {
//         return record.id;
//     }}
//     url={ENV.API_URL + "/category/637caaa4ef59e8a8cdc9f4b3/service"}
//     method={"get"}
//     isPaginated={true}
// />

// ****************************** USAGE ****************************** //
